import { buildQuery, toCamelCase } from '@/shared/services/Convertor';
import Request from '@/shared/services/Request';
import type {
    InsertProps, UpdateProps, DeleteProps, ResponseType, ResponseCollectionType,
    GeneralResponseType,
} from '@/shared/types/General';
import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { isUndefined } from 'lodash';
import { useQuery, useMutation, UseQueryResult, UseMutationResult } from 'react-query';

export abstract class BaseRepository<Entity extends object = object, EntityCollection extends object[] = object[]> {
    protected endpoint ?: string;
    protected name ?: string;

    protected readonly initialize = () => {
        if (isUndefined(this.endpoint)) {
            throw new Error('Endpoint is not set.');
        }

        if (isUndefined(this.name)) {
            throw new Error('Name is not set.');
        }
    };

    public static create() : BaseRepository<any, any> {
        throw new Error('Function is not implemented.');
    }

    public getName() : string {
        return this.name!;
    }

    protected async make(options ?: { timeout ?: number, accept ?: string, contentType ?: string }) : Promise<AxiosInstance> {
        return Request.create(options?.timeout, options?.accept, options?.contentType);
    }

    public fetch<T = GeneralResponseType>(id : string, type : string, config ?: object, isEnabled = true) : UseQueryResult<T, AxiosError> {
        return useQuery(
            [`${this.name}Fetch`, id, type],
            async () : Promise<T> => {
                return toCamelCase<T>(
                    (await (await this.make()).get<ResponseType>(`${this.endpoint}/${id}?type=${type}`, config)).data,
                );
            },
            { enabled: isEnabled },
        );
    }

    public fetchAll<T>(searchQuery : string = '', config ?: object, isEnabled = true) : UseQueryResult<T, AxiosError> {
        return useQuery(
            [`${this.name}FetchAll`, searchQuery],
            async () : Promise<T> => {
                return toCamelCase<T>(
                    (await (await this.make()).get<ResponseCollectionType>(this.endpoint + searchQuery, config)).data,
                );
            },
            { enabled: isEnabled },
        );
    }

    public fetchTable<T>(
        type : string,
        columnFilters : ColumnFiltersState,
        globalFilter : string,
        sorting : SortingState,
        pagination : PaginationState,
        endpoint ?: string,
        config ?: object,
    ) : UseQueryResult<T> {
        const query = buildQuery({
            columnFilters: JSON.stringify(columnFilters),
            globalFilter: globalFilter,
            sorting: JSON.stringify(sorting),
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            type: type,
        });
        
        return useQuery(
            [`${this.name}FetchTable`, type, endpoint ?? this.endpoint, columnFilters, globalFilter, sorting, pagination],
            async () : Promise<T> => {
                return toCamelCase<T>(
                    (await (await this.make()).get<ResponseCollectionType>(`${endpoint ? endpoint : this.endpoint}${query}`, config)).data,
                );
            },
        );
    }

    // TODO: should return inserted id or full object?
    public insert(config ?: object) : UseMutationResult<AxiosResponse<Entity, any>, AxiosError<GeneralResponseType>, InsertProps<Entity>, void> {
        return useMutation({
            mutationFn: async (data : InsertProps) => {
                return (await (await this.make()).postForm(
                    `${this.endpoint}`, data.data, config,
                ));
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    }

    // TODO: should return updated id or full object?
    public update<T = GeneralResponseType>(config ?: object) : UseMutationResult<AxiosResponse<T, any>, AxiosError<GeneralResponseType>, UpdateProps<any>, void> {
        return useMutation({
            mutationFn: async ({ id, data } : UpdateProps) => {
                return (await (await this.make()).put<T>(`${this.endpoint}/${id}`, data, config));
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    }

    // TODO: should return deleted id?
    public delete(config ?: object) : UseMutationResult<AxiosResponse<Entity, any>, AxiosError<GeneralResponseType>, DeleteProps, void> {
        return useMutation({
            mutationFn: async ({ id } : DeleteProps) => {
                return (await (await this.make()).delete<Entity>(`${this.endpoint}/${id}`, config));
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    }

    /**
     * Download file with authentication
     * @param {string} url File URL
     * @param {string} fileName Optional downloaded file name
     * @param {number} timeout Optional request timeout in milliseconds
     * @return {Promise<void>} Resolves after starting the browser download
     */
    public async downloadFile(url : string, fileName ?: string, timeout ?: number) : Promise<void> {
        const response = await (await this.make({ accept: '*/*', timeout })).get(url, { responseType: 'blob' });

        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;

        if (fileName) {
            link.setAttribute('download', fileName);
        } else {
            const contentDisposition = response.headers['content-disposition'];
            let name = 'download';

            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?(.+?)"?($|;)/i);

                if (fileNameMatch?.[1]) {
                    name = fileNameMatch[1];
                }
            }
            link.setAttribute('download', name);
        }

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    }
}

export default BaseRepository;
